/**
 * sourceRegistry.ts
 *
 * Single source of truth for all external source type metadata.
 * Consolidates icon mappings, labels, colors, external URLs, and capabilities
 * that were previously scattered across useSources.js, SourcesBar.vue,
 * CommunitySource.vue, and types.js.
 */

import type { RecordContactFieldsFragment, RecordFieldsFragment } from "@auger/core/gql/types"
import { capitalize } from "@auger/ui/utils/string"

// Source cards also render partial records in the admin viewer. Only identity
// is required; the optional display fields keep their generated fragment types.
export interface SourceCardRecord extends Pick<RecordFieldsFragment, "id" | "type"> {
  name?: RecordFieldsFragment["name"]
  data?: RecordFieldsFragment["data"]
  avatar?: RecordFieldsFragment["avatar"]
  contacts?: Pick<RecordContactFieldsFragment, "contact_type" | "value">[]
}

// Record types that support sources
export type SourceRecordType = "person" | "organization" | "event" | "deal" | "document"

/**
 * Configuration for a single source type.
 */
export interface SourceTypeConfig {
  // Identity
  key: string // Short key: "pipedrive", "airtable"
  type: string // Full type: "pipedrive_person", "airtable_organization"
  recordType: SourceRecordType // Which record type this source applies to

  // Display
  label: string // Human-readable: "Pipedrive", "Airtable"
  icon: string | [string, string] // FontAwesome icon or brand icon tuple
  color?: string // Brand color for styling

  // External Links
  externalUrlTemplate?: string // URL template with {id} placeholder

  // Capabilities
  isSearchable: boolean // Can search for unlinked sources
  hasFetchQuery: boolean // Has a detail fetch query
  hasComponent: boolean // Has a dedicated display component

  // Component reference (for dynamic component resolution)
  componentName?: string // e.g., "PipedrivePersonSource"
}

/**
 * The complete registry of all source types.
 * Keyed by full source type (e.g., "pipedrive_person").
 */
export const SOURCE_REGISTRY: Record<string, SourceTypeConfig> = {
  // ============================================
  // PERSON SOURCES
  // ============================================

  pipedrive_person: {
    key: "pipedrive",
    type: "pipedrive_person",
    recordType: "person",
    label: "Pipedrive",
    icon: ["fab", "product-hunt"],
    color: "#4a689b",
    externalUrlTemplate: "https://heavybit.pipedrive.com/person/{id}",
    isSearchable: true,
    hasFetchQuery: true,
    hasComponent: true,
    componentName: "PipedrivePersonSource",
  },

  pipedrive_user_person: {
    key: "pipedrive_user",
    type: "pipedrive_user_person",
    recordType: "person",
    label: "Pipedrive User",
    icon: "user-lock",
    color: "#4a689b",
    externalUrlTemplate: "https://heavybit.pipedrive.com/users/details/{id}",
    isSearchable: true,
    hasFetchQuery: true,
    hasComponent: true,
    componentName: "PipedriveUserSource",
  },

  airtable_person: {
    key: "airtable",
    type: "airtable_person",
    recordType: "person",
    label: "Airtable",
    icon: "table",
    color: "#245b71",
    externalUrlTemplate: "https://airtable.com/appJOfBzzLguLd91L/{id}",
    isSearchable: true,
    hasFetchQuery: true,
    hasComponent: true,
    componentName: "AirtablePersonSource",
  },

  hubspot_person: {
    key: "hubspot",
    type: "hubspot_person",
    recordType: "person",
    label: "HubSpot",
    icon: ["fab", "hubspot"],
    color: "#ff7a59",
    externalUrlTemplate: "https://app.hubspot.com/contacts/44308902/record/0-1/{id}",
    isSearchable: true,
    hasFetchQuery: true,
    hasComponent: true,
    componentName: "HubSpotPersonSource",
  },

  luma_person: {
    key: "luma",
    type: "luma_person",
    recordType: "person",
    label: "Luma",
    icon: "calendar-check",
    color: "#9b51e0",
    externalUrlTemplate: "https://lu.ma/user/{id}",
    isSearchable: true,
    hasFetchQuery: true,
    hasComponent: true,
    componentName: "LumaEventGuest",
  },

  mailchimp_person: {
    key: "mailchimp",
    type: "mailchimp_person",
    recordType: "person",
    label: "Mailchimp",
    icon: ["fab", "mailchimp"],
    color: "#ffb23e",
    externalUrlTemplate: "https://us20.admin.mailchimp.com/lists/members/view?id={id}",
    isSearchable: true,
    hasFetchQuery: true,
    hasComponent: true,
    componentName: "MailchimpListMember",
  },

  slack_person: {
    key: "slack",
    type: "slack_person",
    recordType: "person",
    label: "Slack",
    icon: ["fab", "slack"],
    color: "#611f69",
    externalUrlTemplate: undefined, // No direct external URL for Slack users
    isSearchable: true,
    hasFetchQuery: true,
    hasComponent: true,
    componentName: "SlackPersonSource",
  },

  sanity_person: {
    key: "sanity",
    type: "sanity_person",
    recordType: "person",
    label: "Sanity",
    icon: "s",
    color: "#f03e2f",
    externalUrlTemplate: "https://heavybit.sanity.studio/desk/person;{id}",
    isSearchable: true,
    hasFetchQuery: true,
    hasComponent: true,
    componentName: "SanityPersonSource",
  },

  harmonic_person: {
    key: "harmonic",
    type: "harmonic_person",
    recordType: "person",
    label: "Harmonic",
    icon: "signal",
    color: "#6f3ff5",
    externalUrlTemplate: "https://console.harmonic.ai/dashboard/person/{id}",
    isSearchable: true,
    hasFetchQuery: false,
    hasComponent: true,
    componentName: "HarmonicPersonSource",
  },

  // ============================================
  // ORGANIZATION SOURCES
  // ============================================

  pipedrive_organization: {
    key: "pipedrive",
    type: "pipedrive_organization",
    recordType: "organization",
    label: "Pipedrive",
    icon: ["fab", "product-hunt"],
    color: "#4a689b",
    externalUrlTemplate: "https://heavybit.pipedrive.com/organization/{id}",
    isSearchable: true,
    hasFetchQuery: true,
    hasComponent: true,
    componentName: "PipedriveOrganizationSource",
  },

  pipedrive_deal: {
    key: "pipedrive",
    type: "pipedrive_deal",
    recordType: "deal",
    label: "Pipedrive",
    icon: ["fab", "product-hunt"],
    color: "#4a689b",
    externalUrlTemplate: "https://heavybit.pipedrive.com/deal/{id}",
    isSearchable: false,
    hasFetchQuery: false,
    hasComponent: false,
  },

  airtable_organization: {
    key: "airtable",
    type: "airtable_organization",
    recordType: "organization",
    label: "Airtable",
    icon: "table",
    color: "#245b71",
    externalUrlTemplate: "https://airtable.com/appJOfBzzLguLd91L/{id}",
    isSearchable: true,
    hasFetchQuery: true,
    hasComponent: true,
    componentName: "AirtableOrganizationSource",
  },

  hubspot_organization: {
    key: "hubspot",
    type: "hubspot_organization",
    recordType: "organization",
    label: "HubSpot",
    icon: ["fab", "hubspot"],
    color: "#ff7a59",
    externalUrlTemplate: "https://app.hubspot.com/contacts/44308902/record/0-2/{id}",
    isSearchable: true,
    hasFetchQuery: true,
    hasComponent: true,
    componentName: "HubSpotOrganizationSource",
  },

  slack_organization: {
    key: "slack",
    type: "slack_organization",
    recordType: "organization",
    label: "Slack",
    icon: ["fab", "slack"],
    color: "#611f69",
    externalUrlTemplate: undefined,
    isSearchable: false, // Not searchable
    hasFetchQuery: false, // No fetch query
    hasComponent: false, // No component
  },

  sanity_organization: {
    key: "sanity",
    type: "sanity_organization",
    recordType: "organization",
    label: "Sanity",
    icon: "s",
    color: "#f03e2f",
    externalUrlTemplate: "https://studio.heavybit.com/content/community;allOrganizations;{id}",
    isSearchable: true,
    hasFetchQuery: true,
    hasComponent: true,
    componentName: "SanityOrganizationSource",
  },

  standard_metrics_organization: {
    key: "standard_metrics",
    type: "standard_metrics_organization",
    recordType: "organization",
    label: "Standard Metrics",
    icon: "chart-line",
    color: "#6366f1",
    externalUrlTemplate: undefined,
    isSearchable: true,
    hasFetchQuery: true,
    hasComponent: true,
    componentName: "StandardMetricsOrganizationSource",
  },

  harmonic_organization: {
    key: "harmonic",
    type: "harmonic_organization",
    recordType: "organization",
    label: "Harmonic",
    icon: "signal",
    color: "#6f3ff5",
    externalUrlTemplate: "https://console.harmonic.ai/dashboard/company/{id}",
    isSearchable: true,
    hasFetchQuery: false,
    hasComponent: true,
    componentName: "HarmonicOrganizationSource",
  },

  // ============================================
  // EVENT SOURCES
  // ============================================

  gcal_event: {
    key: "gcal",
    type: "gcal_event",
    recordType: "event",
    label: "Google Calendar",
    icon: ["fab", "google"],
    color: "#4285f4",
    isSearchable: false,
    hasFetchQuery: false,
    hasComponent: false, // Falls back to RawSource
  },

  luma_event: {
    key: "luma",
    type: "luma_event",
    recordType: "event",
    label: "Luma",
    icon: "calendar-check",
    color: "#9b51e0",
    externalUrlTemplate: "https://lu.ma/{id}",
    isSearchable: true,
    hasFetchQuery: true,
    hasComponent: true,
    componentName: "LumaEventSource",
  },

  airtable_event: {
    key: "airtable",
    type: "airtable_event",
    recordType: "event",
    label: "Airtable",
    icon: "table",
    color: "#245b71",
    externalUrlTemplate: "https://airtable.com/appJOfBzzLguLd91L/{id}",
    isSearchable: true,
    hasFetchQuery: false, // No dedicated fetch query
    hasComponent: false, // Falls back to RawSource
  },

  airtable_global_event: {
    key: "airtable_global",
    type: "airtable_global_event",
    recordType: "event",
    label: "Airtable Global Events",
    icon: "table",
    color: "#245b71",
    externalUrlTemplate: "https://airtable.com/appJOfBzzLguLd91L/{id}",
    isSearchable: true,
    hasFetchQuery: false, // No dedicated fetch query
    hasComponent: false, // Falls back to RawSource
  },

  sanity_event: {
    key: "sanity",
    type: "sanity_event",
    recordType: "event",
    label: "Sanity",
    icon: "s",
    color: "#f03e2f",
    externalUrlTemplate: "https://heavybit.sanity.studio/desk/event;{id}",
    isSearchable: true,
    hasFetchQuery: false, // No dedicated fetch query
    hasComponent: false, // Falls back to RawSource
  },

  sanity_devguild_event: {
    key: "sanity_devguild",
    type: "sanity_devguild_event",
    recordType: "event",
    label: "Sanity DevGuild",
    icon: "s",
    color: "#f03e2f",
    externalUrlTemplate: "https://heavybit.sanity.studio/desk/devGuildEvent;{id}",
    isSearchable: true,
    hasFetchQuery: false, // No dedicated fetch query
    hasComponent: false, // Falls back to RawSource
  },

  // ============================================
  // DOCUMENT SOURCES (saved emails & threads)
  // ============================================
  // Identity convention: `email_message` carries the RFC 5322 Message-ID —
  // the same value in every mailbox, so it's the canonical, provider-agnostic
  // identity and dedupe key for a saved email. The gmail_* types are
  // per-mailbox decorations (Gmail ids differ between users' mailboxes).

  email_message: {
    key: "email",
    type: "email_message",
    recordType: "document",
    label: "Email",
    icon: "envelope",
    // rfc822msgid: search resolves the message in *any* user's own mailbox
    externalUrlTemplate: "https://mail.google.com/mail/u/0/#search/rfc822msgid:{id}",
    isSearchable: false,
    hasFetchQuery: false,
    hasComponent: false, // Falls back to RawSource
  },

  gmail_message: {
    key: "gmail",
    type: "gmail_message",
    recordType: "document",
    label: "Gmail",
    icon: ["fab", "google"],
    color: "#4285f4",
    // Gmail ids are mailbox-scoped: the link only resolves for the user
    // whose mailbox the message was saved from
    externalUrlTemplate: "https://mail.google.com/mail/u/0/#all/{id}",
    isSearchable: false,
    hasFetchQuery: false,
    hasComponent: false, // Falls back to RawSource
  },

  gmail_thread: {
    key: "gmail_thread",
    type: "gmail_thread",
    recordType: "document",
    label: "Gmail Thread",
    icon: ["fab", "google"],
    color: "#4285f4",
    externalUrlTemplate: "https://mail.google.com/mail/u/0/#all/{id}",
    isSearchable: false,
    hasFetchQuery: false,
    hasComponent: false, // Falls back to RawSource
  },

  pipedrive_mail_message: {
    key: "pipedrive_mail",
    type: "pipedrive_mail_message",
    recordType: "document",
    label: "Pipedrive Email",
    icon: ["fab", "product-hunt"],
    color: "#4a689b",
    isSearchable: false,
    hasFetchQuery: false,
    hasComponent: false, // Falls back to RawSource
  },

  pipedrive_mail_thread: {
    key: "pipedrive_mail_thread",
    type: "pipedrive_mail_thread",
    recordType: "document",
    label: "Pipedrive Thread",
    icon: ["fab", "product-hunt"],
    color: "#4a689b",
    isSearchable: false,
    hasFetchQuery: false,
    hasComponent: false, // Falls back to RawSource
  },
}

// ============================================
// UTILITY FUNCTIONS
// ============================================

/**
 * Get the configuration for a source type.
 */
export function getSourceConfig(sourceType: string): SourceTypeConfig | undefined {
  return SOURCE_REGISTRY[sourceType]
}

/**
 * Get the icon for a source type.
 * Returns a fallback icon if the source type is unknown.
 */
export function getSourceIcon(sourceType: string): string | [string, string] {
  return SOURCE_REGISTRY[sourceType]?.icon ?? "link"
}

/**
 * Get a human-readable label for a source type.
 * Falls back to capitalizing the first segment of the source type.
 */
export function getSourceLabel(sourceType: string): string {
  const config = SOURCE_REGISTRY[sourceType]
  if (config) return config.label

  // Fallback: capitalize first segment
  if (!sourceType) return "Unknown"
  const base = sourceType.split("_")[0]!
  return capitalize(base)
}

/**
 * Get the brand color for a source type.
 */
export function getSourceColor(sourceType: string): string | undefined {
  return SOURCE_REGISTRY[sourceType]?.color
}

/**
 * Get the external URL for a source.
 * Returns null if no template is defined or the source is invalid.
 */
export function getSourceExternalUrl(source: {
  source_type: string
  source_id: string
}): string | null {
  if (!source?.source_type || !source?.source_id) return null

  const config = SOURCE_REGISTRY[source.source_type]
  if (!config?.externalUrlTemplate) return null

  // Encode the id: RFC 5322 Message-IDs contain <, > and @. Numeric and
  // alphanumeric ids used by other source types pass through unchanged.
  return config.externalUrlTemplate.replace("{id}", encodeURIComponent(source.source_id))
}

/**
 * Get the short source key from a full source type.
 * e.g., "pipedrive_person" -> "pipedrive"
 * e.g., "pipedrive_user_person" -> "pipedrive_user"
 */
export function getSourceKey(sourceType: string): string {
  const config = SOURCE_REGISTRY[sourceType]
  if (config) return config.key

  // Fallback: remove the record type suffix
  if (!sourceType) return ""
  const parts = sourceType.split("_")
  if (parts.length < 2) return sourceType
  parts.pop() // Remove the record type
  return parts.join("_")
}

/**
 * Resolve a short source key to the full source type for a given record type.
 * e.g., ("person", "pipedrive") -> "pipedrive_person"
 * e.g., ("person", "pipedrive_user") -> "pipedrive_user_person"
 *
 * Returns null when no (key, recordType) pair exists in the registry — callers
 * must skip the source rather than fabricate a malformed source_type.
 */
export function resolveSourceType(recordType: string, sourceKey: string): string | null {
  const match = Object.values(SOURCE_REGISTRY).find(
    (config) => config.key === sourceKey && config.recordType === recordType
  )
  return match?.type ?? null
}

/**
 * Get all source configurations for a record type.
 */
export function getSourcesForRecordType(recordType: string): SourceTypeConfig[] {
  return Object.values(SOURCE_REGISTRY).filter((config) => config.recordType === recordType)
}

/**
 * Get searchable source configurations for a record type.
 * Returns only sources that have isSearchable: true.
 */
export function getSearchableSourcesForRecordType(recordType: string): SourceTypeConfig[] {
  return Object.values(SOURCE_REGISTRY).filter(
    (config) => config.recordType === recordType && config.isSearchable
  )
}

/**
 * Get all source types (full type strings) for a record type.
 * Used for backwards compatibility with sourceConfig.sourceTypes.
 */
export function getSourceTypesForRecordType(recordType: string): string[] {
  return getSourcesForRecordType(recordType).map((config) => config.type)
}

/**
 * Get all searchable source keys for a record type.
 * Used for backwards compatibility with sourceConfig.searchableSourceTypes.
 */
export function getSearchableSourceKeysForRecordType(recordType: string): string[] {
  return getSearchableSourcesForRecordType(recordType).map((config) => config.key)
}

/**
 * Get the component name for a source type.
 * Returns "RawSource" if no dedicated component exists.
 */
export function getSourceComponentName(sourceType: string): string {
  return SOURCE_REGISTRY[sourceType]?.componentName ?? "RawSource"
}
